import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { resetState } from "../../Redux/actions/resetAction";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Menu,
  MenuItem,
  Select,
  Typography,
  useTheme,
  lighten,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import http from "../../utils/http";
import { AxiosResponse } from "axios";
import { toastMessage } from "../../utils/toast";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { setStoredLanguage } from "../../i18n/language";
import { setStateToggle } from "../../Redux/reducers/studySlice";

const LogoutContainer = () => {
  const { t } = useTranslation("header");
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.user.role);
  const [showLangDialog, setShowLangDialog] = useState(false);
  const [lang, setLang] = useState("en");
  const [langLoading, setLangLoading] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const firstName = localStorage.getItem("first-name");
  const lastName = localStorage.getItem("last-name");
  const getInitials = () => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial || "U";
  };

  const openLanguageDialog = async () => {
    try {
      setShowLangDialog(true);
      setLangLoading(true);
      const res: AxiosResponse = await http.get("/user/auth/profile");
      const apiLang = res?.data?.data?.lang;
      if (apiLang === "en" || apiLang === "es") {
        setLang(apiLang);
      }
    } catch {
      // fall back to current i18n language
      setLang(i18n.language?.startsWith("es") ? "es" : "en");
    } finally {
      setLangLoading(false);
    }
  };

  const closeLanguageDialog = () => {
    setShowLangDialog(false);
  };

  const updateLanguage = async () => {
    try {
      setLangLoading(true);
      await http.patch("/user/auth/profile", { lang });
      await i18n.changeLanguage(lang);
      setStoredLanguage(lang as "en" | "es");
      toastMessage("success", t("LANGUAGE_UPDATED"));
      dispatch(setStateToggle())
      closeLanguageDialog();
    } catch {
      toastMessage("error", t("LANGUAGE_UPDATE_FAILED"));
    } finally {
      setLangLoading(false);
    }
  };

  const logout = async () => {
    try {
      let url = "";
      if (role === "participant") {
        url = "/participant/auth/logout";
      } else {
        url = "/user/auth/logout";
      }
      let res: AxiosResponse;
      res = await http.post(url);
      toastMessage("success", res?.data?.message);
    } catch (err) {
      console.log(err);
    } finally {
      localStorage.clear();
      dispatch(resetState());
      if (role === "participant") {
        navigate("/auth/participant/login");
      } else {
        navigate("/auth/login");
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: lighten(theme.palette.primary.main, 0.85),
            color: theme.palette.primary.main,
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {getInitials()}
        </Avatar>
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {role !== "participant" && (
          <MenuItem onClick={() => navigate("/profile/change-password")}>
            {t("CHANGE_PASSWORD")}
          </MenuItem>
        )}
        {role !== "participant" && (
          <MenuItem
            onClick={() => {
              handleClose();
              openLanguageDialog();
            }}
          >
            {t("PROFILE_LANGUAGE")}
          </MenuItem>
        )}
        <MenuItem onClick={logout}>{t("LOGOUT")}</MenuItem>
      </Menu>
      <Dialog open={showLangDialog} onClose={closeLanguageDialog} fullWidth maxWidth="xs">
        <DialogTitle>{t("PROFILE_LANGUAGE")}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("LANGUAGE")}
            </Typography>
            <Select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              disabled={langLoading}
            >
              <MenuItem value="en">{t("LANGUAGE_ENGLISH")}</MenuItem>
              <MenuItem value="es">{t("LANGUAGE_SPANISH")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLanguageDialog} variant="outlined" disabled={langLoading}>
            {t("GO_LIVE_MODAL.CANCEL")}
          </Button>
          <Button onClick={updateLanguage} variant="contained" disabled={langLoading}>
            {t("UPDATE_LANGUAGE")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LogoutContainer;
